import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import { DefaultDark } from 'survey-core/themes';
import surveyTheme from '../../surveyTheme';
import 'survey-core/survey-core.min.css';
import {
    Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
    Eye, ChevronUp, ChevronDown, X, Save, AlertCircle, Link,
    Calendar, RefreshCw, CheckCircle2, CircleDashed, UserX, Users,
    ListChecks, Database, FileText, TrendingUp, ShieldCheck, ChevronsUpDown,
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
    Legend, ResponsiveContainer,
} from 'recharts';
import api from '../../api/api';
import CSIManagement from './CSIManagement';

// ─── Constants ────────────────────────────────────────────────────────────────

const SURVEY_TYPES = [
    { value: 'onboarding', label: 'Onboarding' },
    { value: 'feedback',   label: 'User Feedback' },
    { value: 'feature',    label: 'Feature' },
    { value: 'cohort',     label: 'Cohort' },
    { value: 'coach',      label: 'Coach' },
];

const TRIGGER_TYPES = [
    { value: 'signup',          label: 'After Signup',           hasValue: false },
    { value: 'login_count',     label: 'After N Logins',         hasValue: true,  placeholder: 'e.g. 2' },
    { value: 'days',            label: 'After N Days',           hasValue: true,  placeholder: 'e.g. 7' },
    { value: 'progress_report', label: 'On Progress Report View', hasValue: false },
    { value: 'manual',          label: 'Manual / Direct Link',   hasValue: false },
];

const QUESTION_TYPES = [
    { value: 'text',       label: 'Text Input (single line)' },
    { value: 'comment',    label: 'Long Text Input (multi line)' },
    { value: 'rating',     label: 'Rating (1–5 Stars)' },
    { value: 'radiogroup', label: 'Multiple Choice (single)' },
    { value: 'checkbox',   label: 'Multiple Choice (multi)' },
];

const EMPTY_FORM = {
    title: '',
    description: '',
    surveyType: 'onboarding',
    triggerType: 'signup',
    triggerValue: '',
    isActive: true,
};

const EMPTY_QUESTION = {
    name: '',
    title: '',
    type: 'text',
    isRequired: false,
    choices: '',  // newline-separated string for the UI; converted to array on save
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildSurveyJSON(title, description, questions) {
    return {
        title: title || 'Untitled Survey',
        description: description || undefined,
        pages: [{
            name: 'page1',
            elements: questions.map((q, idx) => {
                const el = {
                    type: q.type,
                    name: q.name || `q${idx + 1}`,
                    title: q.title,
                    isRequired: q.isRequired,
                };
                if (q.type === 'radiogroup' || q.type === 'checkbox') {
                    el.choices = q.choices
                        ? q.choices.split('\n').map(c => c.trim()).filter(Boolean)
                        : [];
                }
                if (q.type === 'rating') {
                    el.rateMin = 1;
                    el.rateMax = 5;
                    el.minRateDescription = 'Poor';
                    el.maxRateDescription = 'Excellent';
                }
                if (q.type === 'comment') {
                    // Multi-line textarea that wraps and grows with the answer
                    el.rows = 3;
                    el.autoGrow = true;
                }
                return el;
            }),
        }],
    };
}

function parseSurveyJSONToQuestions(surveyJSON) {
    if (!surveyJSON || !surveyJSON.pages || !surveyJSON.pages[0]) return [];
    return (surveyJSON.pages[0].elements || []).map(el => ({
        name: el.name || '',
        title: el.title || '',
        type: el.type || 'text',
        isRequired: el.isRequired || false,
        choices: Array.isArray(el.choices) ? el.choices.join('\n') : (el.choices || ''),
    }));
}

// ─── Reporting helpers & UI atoms (design from Evan's SurveyManagement.jsx) ───

function getQuestions(surveyJSON) {
    return surveyJSON?.pages?.[0]?.elements || surveyJSON?.elements || [];
}

function respondentName(r) {
    return r.FirstName ? `${r.FirstName} ${r.LastName || ''}`.trim() : 'Anonymous';
}

function isAnonymous(r) {
    return !r.ClientID && !r.AdminID;
}

function responseStatus(r, questions) {
    const answered = questions.filter(q => {
        const v = (r.ResponseJSON || {})[q.name];
        return v !== undefined && v !== null && v !== '';
    }).length;
    return answered >= questions.length ? 'Complete' : 'Partial';
}

function questionTypeLabel(type) {
    if (type === 'rating') return 'Rating';
    if (type === 'radiogroup' || type === 'checkbox') return 'Multiple Choice';
    return 'Text';
}

// Design tokens per mode — light values are Evan's spec, dark values are
// the closest slate equivalents
function reportTokens(isDarkMode) {
    return isDarkMode ? {
        bg: '#0f172a', surface: '#1e293b', border: '#334155',
        ink: '#f1f5f9', muted: '#94a3b8',
        primary: '#2F7D52',
        mintBg: 'rgba(30,142,76,0.18)', mintText: '#4ade80',
        greyBg: 'rgba(148,163,184,0.15)',
        amberBg: 'rgba(180,83,9,0.18)', amberText: '#fbbf24',
        blueBg: 'rgba(37,99,235,0.18)', blueText: '#93c5fd',
        redBg: 'rgba(220,38,38,0.18)', redText: '#fca5a5',
        purpleBg: 'rgba(124,58,237,0.18)', purpleText: '#c4b5fd',
    } : {
        bg: '#F7F8FA', surface: '#FFFFFF', border: '#E5E7EB',
        ink: '#111827', muted: '#6B7280',
        primary: '#2F7D52',
        mintBg: '#E7F6EC', mintText: '#1E8E4C',
        greyBg: '#F1F2F4',
        amberBg: '#FEF6E7', amberText: '#B45309',
        blueBg: '#EAF1FE', blueText: '#2563EB',
        redBg: '#FDEEEE', redText: '#DC2626',
        purpleBg: '#F3EEFE', purpleText: '#7C3AED',
    };
}

function Pill({ children, bg, color }) {
    return (
        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: bg, color }}>
            {children}
        </span>
    );
}

function StatusPill({ status, tk }) {
    const complete = status === 'Complete';
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
            style={{ background: complete ? tk.mintBg : tk.amberBg, color: complete ? tk.mintText : tk.amberText }}>
            {complete ? <CheckCircle2 size={12} /> : <CircleDashed size={12} />}
            {status}
        </span>
    );
}

function QuietStat({ label, value, sub, tk }) {
    return (
        <div className="rounded-lg border px-4 py-3" style={{ borderColor: tk.border, background: tk.surface }}>
            <div className="text-xs font-medium" style={{ color: tk.muted }}>{label}</div>
            <div className="mt-1 text-xl font-semibold" style={{ color: tk.ink, fontVariantNumeric: 'tabular-nums' }}>
                {value} {sub && <span className="text-sm font-normal" style={{ color: tk.muted }}>{sub}</span>}
            </div>
            <div className="mt-6 text-3xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            <div className="mt-1 text-sm opacity-90">{label}</div>
        </div>
    );
}

function GradientStat({ icon: Icon, cornerIcon: Corner, value, label, gradient }) {
    return (
        <div className="rounded-2xl px-6 py-5 text-white" style={{ background: gradient }}>
            <div className="flex items-start justify-between">
                <Icon size={26} strokeWidth={1.75} />
                <Corner size={18} />
            </div>
            <div className="mt-6 text-3xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            <div className="mt-1 text-sm opacity-90">{label}</div>
        </div>
    );
}

function Chip({ icon: Icon, label, value, bg, color }) {
    return (
        <div className="rounded-xl px-4 py-3.5" style={{ background: bg }}>
            <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color }}>
                <Icon size={15} /> {label}
            </div>
            <div className="mt-1 text-2xl font-bold" style={{ color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
        </div>
    );
}

function sortIcon(dir) {
    if (dir === 'asc') return <ChevronUp size={13} />;
    if (dir === 'desc') return <ChevronDown size={13} />;
    return <ChevronsUpDown size={13} style={{ opacity: 0.4 }} />;
}


function triggerLabel(triggerType, triggerValue) {
    const t = TRIGGER_TYPES.find(x => x.value === triggerType);
    if (!t) return triggerType;
    return t.hasValue && triggerValue ? `${t.label} (${triggerValue})` : t.label;
}

// ─── Question Form (add / edit) ───────────────────────────────────────────────

function QuestionForm({ initial, onSave, onCancel, isDarkMode, existingKeys = [] }) {
    const [q, setQ] = useState(initial || EMPTY_QUESTION);

    const inputCls = `w-full border rounded px-3 py-2 text-sm ${
        isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
    }`;

    const keyTrimmed = q.name.trim();
    const isDuplicateKey = keyTrimmed !== '' && existingKeys.includes(keyTrimmed);

    return (
        <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                    <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">Question Key (unique)</label>
                    <div className="relative">
                        <input
                            className={`w-full border rounded px-3 py-2 text-sm pr-8 ${
                                isDuplicateKey
                                    ? 'border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500'
                                    : isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
                            } ${isDarkMode ? 'bg-slate-700 text-white' : 'bg-white text-slate-900'}`}
                            placeholder="e.g. q1_challenge"
                            value={q.name}
                            onChange={e => setQ({ ...q, name: e.target.value })}
                        />
                        {isDuplicateKey && (
                            <AlertCircle size={15} className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pointer-events-none" />
                        )}
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">Type</label>
                    <select className={inputCls} value={q.type} onChange={e => setQ({ ...q, type: e.target.value })}>
                        {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
            </div>

            <div className="mb-3">
                <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">Question Text</label>
                <input
                    className={inputCls}
                    placeholder="What is your question?"
                    value={q.title}
                    onChange={e => setQ({ ...q, title: e.target.value })}
                />
            </div>

            {(q.type === 'radiogroup' || q.type === 'checkbox') && (
                <div className="mb-3">
                    <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">Answer choices (one per line)</label>
                    <textarea
                        className={`${inputCls} h-24 resize-none`}
                        placeholder={"Option A\nOption B\nOption C"}
                        value={q.choices}
                        onChange={e => setQ({ ...q, choices: e.target.value })}
                    />
                </div>
            )}

            <div className="flex items-center gap-2 mb-4">
                <input
                    type="checkbox"
                    id="q-required"
                    checked={q.isRequired}
                    onChange={e => setQ({ ...q, isRequired: e.target.checked })}
                    className="rounded"
                />
                <label htmlFor="q-required" className="text-sm">Required</label>
            </div>

            <div className="flex gap-2 justify-end">
                <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors">
                    Cancel
                </button>
                <button
                    disabled={isDuplicateKey}
                    onClick={() => {
                        if (!q.title.trim() || isDuplicateKey) return;
                        if (!q.name.trim()) {
                            let n = 1;
                            while (existingKeys.includes(`q${n}`)) n++;
                            onSave({ ...q, name: `q${n}` });
                        } else {
                            onSave(q);
                        }
                    }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Save Question
                </button>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SurveyManagement() {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('surveys'); // 'surveys' | 'builder'

    // ── Survey list state ────────────────────────────────────────────────────
    const [surveys, setSurveys] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // ── Builder state ────────────────────────────────────────────────────────
    const [editingId, setEditingId] = useState(null); // null = create new
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [questions, setQuestions] = useState([]);
    const [showQuestionForm, setShowQuestionForm] = useState(false);
    const [editingQuestionIdx, setEditingQuestionIdx] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // ── Responses tab state ──────────────────────────────────────────────────
    const [responsesTab, setResponsesTab] = useState({ responses: null, loading: false, error: null, search: '', page: 1, pageSize: 10 });

    // ── Reports tab state ────────────────────────────────────────────────────
    const [reportSurveyId, setReportSurveyId] = useState(null);
    const [reportTimeframe, setReportTimeframe] = useState('All Time');
    const [reportTableQuery, setReportTableQuery] = useState('');
    const [reportSort, setReportSort] = useState({ key: 'responses', dir: 'desc' });
    

    // ── Preview ──────────────────────────────────────────────────────────────
    const [previewModel, setPreviewModel] = useState(null);
    const previewTimeout = useRef(null);

    // ── Admin guard ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!user || !user.isAdmin) navigate('/');
    }, [user, navigate]);

    // ── Fetch survey list ────────────────────────────────────────────────────
    const fetchSurveys = useCallback(async () => {
        setListLoading(true);
        setListError(null);
        try {
            const res = await api.get('/survey/admin/surveys');
            setSurveys(res.data.surveys || []);
        } catch (err) {
            setListError(err.response?.data?.error || 'Failed to load surveys');
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSurveys();
    }, [fetchSurveys]);

    const fetchAllResponses = useCallback(async () => {
        setResponsesTab(prev => ({ ...prev, loading: true, error: null }));
        try {
            const res = await api.get('/survey/admin/responses');
            setResponsesTab(prev => ({ ...prev, loading: false, responses: res.data.responses }));
        } catch (err) {
            setResponsesTab(prev => ({ ...prev, loading: false, error: 'Failed to load responses' }));
        }
    }, []);

    // ── Rebuild preview whenever questions or title/desc change ──────────────
    useEffect(() => {
        clearTimeout(previewTimeout.current);
        previewTimeout.current = setTimeout(() => {
            if (questions.length > 0 || formData.title) {
                const json = buildSurveyJSON(formData.title, formData.description, questions);
                const model = new Model(json);
                // Match the public survey page (it forces responsive) so the
                // preview shows exactly what visitors will see
                model.widthMode = "responsive";
                setPreviewModel(model);
            } else {
                setPreviewModel(null);
            }
        }, 300);
        return () => clearTimeout(previewTimeout.current);
    }, [questions, formData.title, formData.description]);

    // ── Apply dark/light theme to preview ────────────────────────────────────
    useEffect(() => {
        if (previewModel) {
            // Light preview uses the shared brand theme so admins see exactly
            // what visitors get; dark mode keeps the stock dark theme
            const theme = isDarkMode ? DefaultDark : surveyTheme;
            previewModel.applyTheme(theme);
        }
    }, [previewModel, isDarkMode]);

    // ── Builder actions ──────────────────────────────────────────────────────

    const openCreate = () => {
        setEditingId(null);
        setFormData(EMPTY_FORM);
        setQuestions([]);
        setShowQuestionForm(false);
        setEditingQuestionIdx(null);
        setSaveError(null);
        setSaveSuccess(false);
        setActiveTab('builder');
    };

    const openEdit = async (survey) => {
        setSaveError(null);
        setSaveSuccess(false);
        try {
            const res = await api.get(`/survey/admin/surveys/${survey.SurveyID}`);
            const s = res.data.survey;
            setEditingId(s.SurveyID);
            setFormData({
                title: s.Title || '',
                description: s.Description || '',
                surveyType: s.SurveyType || 'onboarding',
                triggerType: s.TriggerType || 'manual',
                triggerValue: s.TriggerValue != null ? String(s.TriggerValue) : '',
                isActive: !!s.IsActive,
            });
            setQuestions(parseSurveyJSONToQuestions(s.SurveyJSON));
        } catch (err) {
            setListError('Failed to load survey for editing');
        }
        setShowQuestionForm(false);
        setEditingQuestionIdx(null);
        setActiveTab('builder');
    };

    const handleSave = async () => {
        if (!formData.title.trim()) {
            setSaveError('Survey title is required.');
            return;
        }
        if (questions.length === 0) {
            setSaveError('Add at least one question before saving.');
            return;
        }
        // 'After N logins' / 'After N days' need a whole number of 1 or more (the
        // server checks the same): saved without one, the survey never showed
        const valueTrigger = TRIGGER_TYPES.find(t => t.value === formData.triggerType);
        if (valueTrigger?.hasValue && !/^[1-9]\d*$/.test(String(formData.triggerValue).trim())) {
            setSaveError(`Enter the number of ${formData.triggerType === 'login_count' ? 'logins' : 'days'} (a whole number, 1 or more).`);
            return;
        }

        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        const triggerMeta = TRIGGER_TYPES.find(t => t.value === formData.triggerType);
        const payload = {
            title: formData.title.trim(),
            description: formData.description.trim() || null,
            surveyType: formData.surveyType,
            triggerType: formData.triggerType,
            triggerValue: triggerMeta?.hasValue && formData.triggerValue
                ? parseInt(formData.triggerValue, 10)
                : null,
            isActive: formData.isActive,
            surveyJSON: buildSurveyJSON(formData.title, formData.description, questions),
        };

        try {
            if (editingId) {
                await api.put(`/survey/admin/surveys/${editingId}`, payload);
            } else {
                await api.post('/survey/admin/surveys', payload);
            }
            setSaveSuccess(true);
            fetchSurveys();
            setTimeout(() => {
                setSaveSuccess(false);
                setActiveTab('surveys');
            }, 1200);
        } catch (err) {
            setSaveError(err.response?.data?.error || 'Failed to save survey.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (survey) => {
        try {
            await api.patch(`/survey/admin/surveys/${survey.SurveyID}/toggle`);
            fetchSurveys();
        } catch (err) {
            console.error('Toggle active failed', err);
        }
    };

    const handleDelete = async (survey) => {
        if (!window.confirm(`Delete "${survey.Title}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/survey/admin/surveys/${survey.SurveyID}`);
            fetchSurveys();
        } catch (err) {
            console.error('Delete failed', err);
        }
    };

    // ── Question CRUD helpers ────────────────────────────────────────────────

    const moveQuestion = (idx, direction) => {
        const newQ = [...questions];
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= newQ.length) return;
        [newQ[idx], newQ[swapIdx]] = [newQ[swapIdx], newQ[idx]];
        setQuestions(newQ);
        if (editingQuestionIdx === idx) setEditingQuestionIdx(swapIdx);
        else if (editingQuestionIdx === swapIdx) setEditingQuestionIdx(idx);
    };

    const saveQuestion = (q) => {
        if (editingQuestionIdx !== null) {
            const updated = [...questions];
            updated[editingQuestionIdx] = q;
            setQuestions(updated);
            setEditingQuestionIdx(null);
        } else {
            setQuestions([...questions, q]);
        }
        setShowQuestionForm(false);
    };

    const deleteQuestion = (idx) => {
        setQuestions(questions.filter((_, i) => i !== idx));
    };

    // ── Filtered surveys ─────────────────────────────────────────────────────

    const filteredSurveys = surveys.filter(s =>
        !searchTerm || s.Title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ── Style helpers ────────────────────────────────────────────────────────

    const pageBg   = isDarkMode ? 'bg-slate-900 text-white min-h-screen' : 'bg-slate-50 text-slate-900 min-h-screen';
    const cardBg   = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
    const inputCls = `w-full border rounded-lg px-3 py-2 text-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900'}`;
    const tabActive   = 'border-b-2 border-primary-500 text-primary-600 font-semibold';
    const tabInactive = isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800';

    const triggerMeta = TRIGGER_TYPES.find(t => t.value === formData.triggerType);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className={pageBg}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Page header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-extrabold">Survey Management</h1>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Create and manage in-app surveys for users and coaches.
                        </p>
                    </div>
                    {activeTab !== 'csi' && (
                        <button
                            onClick={openCreate}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors text-sm"
                        >
                            <Plus size={16} />
                            New Survey
                        </button>
                    )}
                </div>

                {/* Tab bar */}
                <div className={`flex gap-6 border-b mb-6 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    {[
                        { key: 'surveys', label: 'Surveys' },
                        { key: 'builder', label: editingId ? 'Edit Survey' : 'Create Survey' },
                        { key: 'responses', label: 'Survey Responses' },
                        { key: 'reporting', label: 'Reporting' },
                        { key: 'csi', label: 'Career Success Index' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => {
                                setActiveTab(tab.key);
                                if (tab.key === 'responses' || tab.key === 'reporting') {
                                    // Entering either data tab always starts fresh
                                    // at "Select a survey…" (the surveys-list eye
                                    // button bypasses this and preselects)
                                    setReportSurveyId(null);
                                    setResponsesTab(prev => ({ ...prev, search: '', page: 1 }));
                                    setReportTableQuery('');
                                    if (!responsesTab.responses) fetchAllResponses();
                                }
                            }}
                            className={`pb-3 text-sm transition-colors ${activeTab === tab.key ? tabActive : tabInactive}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ── TAB: Surveys list ── */}
                {activeTab === 'surveys' && (
                    <div>
                        {/* Search */}
                        <div className="mb-4">
                            <input
                                className={inputCls}
                                placeholder="Search surveys by title…"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {listError && (
                            <div className="flex items-center gap-2 text-red-500 mb-4 text-sm">
                                <AlertCircle size={16} /> {listError}
                            </div>
                        )}

                        {listLoading ? (
                            <div className="flex justify-center py-16">
                                <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                            </div>
                        ) : filteredSurveys.length === 0 ? (
                            <div className={`text-center py-16 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {searchTerm ? 'No surveys match your search.' : 'No surveys yet. Click "New Survey" to get started.'}
                            </div>
                        ) : (
                            <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
                                <table className="min-w-full divide-y divide-slate-200">
                                    <thead>
                                        <tr className={isDarkMode ? 'bg-slate-700' : 'bg-slate-50'}>
                                            {['Title', 'Type', 'Trigger', 'Responses', 'Status', 'Actions'].map(h => (
                                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                                        {filteredSurveys.map(s => (
                                            <tr key={s.SurveyID} className={isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}>
                                                <td className="px-4 py-3 font-medium text-sm">{s.Title}</td>
                                                <td className="px-4 py-3 text-sm capitalize">{s.SurveyType}</td>
                                                <td className="px-4 py-3 text-sm text-slate-500">{triggerLabel(s.TriggerType, s.TriggerValue)}</td>
                                                <td className="px-4 py-3 text-sm">{s.ResponseCount ?? 0}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                        s.IsActive
                                                            ? 'bg-primary-100 text-primary-700'
                                                            : 'bg-slate-100 text-slate-500'
                                                    }`}>
                                                        {s.IsActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => openEdit(s)}
                                                            title="Edit"
                                                            className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-500 hover:text-primary-600"
                                                        >
                                                            <Pencil size={15} />
                                                        </button>
                                                        {s.IsActive ? (
                                                            <button
                                                                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/survey/${s.SurveyID}`)}
                                                                title="Copy public URL"
                                                                className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-500 hover:text-primary-600"
                                                            >
                                                                <Link size={15} />
                                                            </button>
                                                        ) : null}
                                                        <button
                                                            onClick={() => {
                                                                setReportSurveyId(s.SurveyID);
                                                                setResponsesTab(prev => ({ ...prev, page: 1 }));
                                                                setActiveTab('responses');
                                                                if (!responsesTab.responses) fetchAllResponses();
                                                            }}
                                                            title="View responses"
                                                            className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-500 hover:text-primary-600"
                                                        >
                                                            <Eye size={15} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleActive(s)}
                                                            title={s.IsActive ? 'Deactivate' : 'Activate'}
                                                            className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-500"
                                                        >
                                                            {s.IsActive ? <ToggleRight size={15} className="text-primary-600" /> : <ToggleLeft size={15} />}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(s)}
                                                            title="Delete"
                                                            className="p-1.5 rounded-lg hover:bg-red-100 transition-colors text-slate-400 hover:text-red-600"
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ── TAB: Survey Responses ── */}
                {activeTab === 'responses' && (
                    <div>
                        {responsesTab.loading && (
                            <div className="flex justify-center py-16">
                                <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                            </div>
                        )}
                        {responsesTab.error && (
                            <div className="flex items-center gap-2 text-red-500 text-sm">
                                <AlertCircle size={16} /> {responsesTab.error}
                            </div>
                        )}
                        {responsesTab.responses && (() => {
                            const tk = reportTokens(isDarkMode);
                            const all = responsesTab.responses;
                            const counts = {};
                            all.forEach(r => { counts[r.SurveyID] = (counts[r.SurveyID] || 0) + 1; });
                            const surveyOptions = (surveys.length
                                ? surveys.map(s => ({ id: s.SurveyID, title: s.Title }))
                                : [...new Map(all.map(r => [r.SurveyID, { id: r.SurveyID, title: r.SurveyTitle }])).values()]
                            ).map(o => ({ ...o, count: counts[o.id] || 0 }));

                            const selectedId = reportSurveyId;
                            const surveyRows = selectedId ? all.filter(r => r.SurveyID === selectedId) : [];
                            const surveyMeta = surveys.find(s => s.SurveyID === selectedId);
                            const questions = getQuestions(surveyRows[0]?.SurveyJSON || surveyMeta?.SurveyJSON);

                            const completeCount = surveyRows.filter(r => responseStatus(r, questions) === 'Complete').length;
                            const anonCount = surveyRows.filter(isAnonymous).length;
                            const completionRate = surveyRows.length ? Math.round((completeCount / surveyRows.length) * 100) : 0;

                            const query = responsesTab.search.trim().toLowerCase();
                            const scope = selectedId ? surveyRows : all;
                            const listRows = !query ? scope : scope.filter(r => respondentName(r).toLowerCase().includes(query));

                            const PAGE_SIZE = responsesTab.pageSize;
                            const totalPages = Math.max(Math.ceil(listRows.length / PAGE_SIZE), 1);
                            const page = Math.min(responsesTab.page, totalPages);
                            const pageItems = listRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
                            const surveyCounters = {};
                            listRows.forEach(r => {
                                surveyCounters[r.SurveyID] = (surveyCounters[r.SurveyID] || 0) + 1;
                                r._surveyIdx = surveyCounters[r.SurveyID];
                            });

                            return (
                                <div className="space-y-4">
                                    {/* Controls: survey select + respondent search */}
                                    <div className="flex flex-wrap items-center gap-3">
                                        <select
                                            className={`${inputCls} max-w-xs`}
                                            value={selectedId ?? ''}
                                            onChange={e => {
                                                setReportSurveyId(e.target.value === '' ? null : Number(e.target.value));
                                                setResponsesTab(prev => ({ ...prev, page: 1 }));
                                            }}
                                        >
                                            <option value="">Select a survey…</option>
                                            {surveyOptions.map(o => (
                                                <option key={o.id} value={o.id}>
                                                    {o.title} · {o.count} response{o.count === 1 ? '' : 's'}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            className={`${inputCls} max-w-[240px]`}
                                            placeholder="Search respondent…"
                                            value={responsesTab.search}
                                            onChange={e => setResponsesTab(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                                        />
                                    </div>

                                    {/* Quiet context strip */}
                                    {selectedId ? (
                                        <div className="grid grid-cols-3 gap-3">
                                            <QuietStat tk={tk} label="Total Responses" value={surveyRows.length} />
                                            <QuietStat tk={tk} label="Completion Rate" value={`${completionRate}%`} />
                                            <QuietStat tk={tk} label="Anonymous" value={anonCount} sub={`of ${surveyRows.length}`} />
                                        </div>
                                    ) : (
                                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Select a survey to see its stats — or search a respondent across all surveys.
                                        </p>
                                    )}

                                    {/* Response cards */}
                                    {listRows.length === 0 && (
                                        <div className="rounded-xl border px-5 py-10 text-center text-sm"
                                            style={{ borderColor: tk.border, color: tk.muted }}>
                                            {query ? `No responses match "${responsesTab.search}".`
                                                : selectedId ? 'No responses yet for this survey.'
                                                : 'No responses found.'}
                                        </div>
                                    )}
                                    {pageItems.map(r => {
                                        const qs = getQuestions(r.SurveyJSON);
                                        const questionMap = Object.fromEntries(qs.map(q => [q.name, q.title]));
                                        const status = responseStatus(r, qs);
                                        const anon = isAnonymous(r);
                                        return (
                                            <div key={r.ResponseID} className={`rounded-2xl border p-5 ${cardBg}`}>
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-sm font-semibold">#{r._surveyIdx} · {r.SurveyTitle}</span>
                                                    <span className="flex items-center gap-3">
                                                        <StatusPill status={status} tk={tk} />
                                                        <span className={`inline-flex items-center gap-1.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                            {anon ? <UserX size={13} /> : <Users size={13} />}
                                                            {respondentName(r)} · {r.SubmittedAt ? new Date(r.SubmittedAt.endsWith('Z') ? r.SubmittedAt : r.SubmittedAt + 'Z').toLocaleString() : 'N/A'}
                                                        </span>
                                                    </span>
                                                </div>
                                                <div className="space-y-2">
                                                    {Object.entries(r.ResponseJSON || {}).map(([key, value]) => (
                                                        <div key={key} className="flex gap-3 text-sm">
                                                            <span className={`font-medium flex-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                                {questionMap[key] || key}
                                                            </span>
                                                            <span className={`flex-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                {Array.isArray(value) ? value.join(', ') : typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {totalPages > 1 && (
                                        <div className="flex justify-center gap-1.5 pt-2">
                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                                <button
                                                    key={p}
                                                    onClick={() => setResponsesTab(prev => ({ ...prev, page: p }))}
                                                    className={`px-2.5 py-1 rounded-lg text-sm border transition-colors ${
                                                        p === page
                                                            ? 'bg-primary-600 text-white border-primary-600'
                                                            : isDarkMode
                                                                ? 'border-slate-600 hover:bg-slate-700 text-slate-300'
                                                                : 'border-slate-300 hover:bg-slate-100 text-slate-700'
                                                    }`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* ── TAB: Reporting (design: Evan's SurveyManagement.jsx, mirrors Job Management → Statistics) ── */}
                {activeTab === 'reporting' && (
                    <div>
                        {responsesTab.loading && (
                            <div className="flex justify-center py-16">
                                <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                            </div>
                        )}
                        {responsesTab.responses && (() => {
                            const tk = reportTokens(isDarkMode);
                            const all = responsesTab.responses;
                            const surveyOptions = (surveys.length
                                ? surveys.map(s => ({ id: s.SurveyID, title: s.Title }))
                                : [...new Map(all.map(r => [r.SurveyID, { id: r.SurveyID, title: r.SurveyTitle }])).values()]
                            );

                            // null = "All Surveys" (the default): aggregate every survey
                            const selectedId = reportSurveyId;
                            const surveyMeta = surveys.find(s => s.SurveyID === selectedId);
                            const inTimeframe = r => {
                                if (reportTimeframe === 'All Time') return true;
                                if (!r.SubmittedAt) return false;
                                const days = reportTimeframe === 'This Week' ? 7 : 30;
                                return Date.now() - new Date(r.SubmittedAt.endsWith('Z') ? r.SubmittedAt : r.SubmittedAt + 'Z').getTime() < days * 24 * 3600 * 1000;
                            };
                            const counts = {};
                            all.filter(inTimeframe).forEach(r => { counts[r.SurveyID] = (counts[r.SurveyID] || 0) + 1; });
                            const records = all.filter(r => (!selectedId || r.SurveyID === selectedId) && inTimeframe(r));
                            const questions = selectedId ? getQuestions(records[0]?.SurveyJSON || surveyMeta?.SurveyJSON) : [];

                            // Complete/Partial is judged against the question list of the
                            // survey each response belongs to, so the aggregate view stays
                            // accurate when surveys have different lengths.
                            const questionsBySurvey = new Map();
                            const questionsFor = r => {
                                if (selectedId) return questions;
                                if (!questionsBySurvey.has(r.SurveyID)) questionsBySurvey.set(r.SurveyID, getQuestions(r.SurveyJSON));
                                return questionsBySurvey.get(r.SurveyID);
                            };

                            const completeCount = records.filter(r => responseStatus(r, questionsFor(r)) === 'Complete').length;
                            const partialCount = records.length - completeCount;
                            const anonCount = records.filter(isAnonymous).length;
                            const loggedInCount = records.length - anonCount;
                            const completionRate = records.length ? Math.round((completeCount / records.length) * 100) : 0;
                            const avgAnswered = records.length
                                ? (records.reduce((sum, r) => sum + Object.keys(r.ResponseJSON || {}).length, 0) / records.length).toFixed(1)
                                : '0.0';

                            const byDay = {};
                            records.forEach(r => {
                                if (!r.SubmittedAt) return;
                                const d = new Date(r.SubmittedAt.endsWith('Z') ? r.SubmittedAt : r.SubmittedAt + 'Z');
                                const key = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
                                byDay[key] = byDay[key] || { day: key, ts: d.setHours(0,0,0,0), Complete: 0, Partial: 0 };
                                byDay[key][responseStatus(r, questionsFor(r))] += 1;
                            });
                            const activityData = Object.values(byDay).sort((a, b) => a.ts - b.ts);

                            const questionRows = questions.map(q => {
                                const answered = records.filter(r => {
                                    const v = (r.ResponseJSON || {})[q.name];
                                    return v !== undefined && v !== null && v !== '';
                                });
                                const answerCounts = {};
                                answered.forEach(r => {
                                    const v = (r.ResponseJSON || {})[q.name];
                                    (Array.isArray(v) ? v : [v]).forEach(a => {
                                        answerCounts[a] = (answerCounts[a] || 0) + 1;
                                    });
                                });
                                const top = Object.entries(answerCounts).sort((a, b) => b[1] - a[1])[0];
                                const isText = q.type === 'text' || q.type === 'comment';
                                return {
                                    id: q.name,
                                    question: q.title || q.name,
                                    type: questionTypeLabel(q.type),
                                    responses: answered.length,
                                    topAnswer: isText ? '—' : (top ? String(top[0]) : '—'),
                                    responseRate: records.length ? Math.round((answered.length / records.length) * 100) : 0,
                                };
                            });
                            const tq = reportTableQuery.trim().toLowerCase();
                            const filteredRows = questionRows.filter(r => r.question.toLowerCase().includes(tq));
                            const sortedRows = [...filteredRows].sort((a, b) => {
                                const av = a[reportSort.key], bv = b[reportSort.key];
                                const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
                                return reportSort.dir === 'asc' ? cmp : -cmp;
                            });
                            const toggleSort = key => setReportSort(prev =>
                                prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
                            );

                            const topSurveys = surveyOptions
                                .map(o => ({ ...o, responses: counts[o.id] || 0 }))
                                .sort((a, b) => b.responses - a.responses)
                                .slice(0, 7);

                            return (
                                <div className="space-y-5">
                                    {/* timeframe row */}
                                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border px-5 py-3.5"
                                        style={{ borderColor: tk.border, background: tk.surface }}>
                                        <div className="flex flex-wrap items-center gap-3">
                                            <Calendar size={16} style={{ color: tk.muted }} />
                                            <span className="text-sm" style={{ color: tk.ink }}>Survey:</span>
                                            <select
                                                className={`${inputCls} max-w-[220px]`}
                                                value={selectedId ?? ''}
                                                onChange={e => setReportSurveyId(e.target.value === '' ? null : Number(e.target.value))}
                                            >
                                                <option value="">All Surveys</option>
                                                {surveyOptions.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
                                            </select>
                                            <span className="text-sm ml-1" style={{ color: tk.ink }}>Timeframe:</span>
                                            <select
                                                className={`${inputCls} max-w-[150px]`}
                                                value={reportTimeframe}
                                                onChange={e => setReportTimeframe(e.target.value)}
                                            >
                                                {['All Time', 'This Week', 'This Month'].map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <button
                                            onClick={fetchAllResponses}
                                            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white"
                                            style={{ background: tk.primary }}
                                        >
                                            <RefreshCw size={14} /> Refresh
                                        </button>
                                    </div>

                                    <>
                                            {/* gradient stat cards */}
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                <GradientStat icon={FileText} cornerIcon={TrendingUp}
                                                    value={records.length} label="Total Responses"
                                                    gradient="linear-gradient(135deg, #3EA872, #1F7A4D)" />
                                                <GradientStat icon={ShieldCheck} cornerIcon={Users}
                                                    value={`${completionRate}%`} label="Completion Rate"
                                                    gradient="linear-gradient(135deg, #5D8DF3, #2E5FD1)" />
                                                <GradientStat icon={ListChecks} cornerIcon={CheckCircle2}
                                                    value={avgAnswered} label="Avg. Questions Answered"
                                                    gradient="linear-gradient(135deg, #A57BF0, #7440D6)" />
                                            </div>

                                            {/* breakdown chips */}
                                            <div className="rounded-xl border p-5" style={{ borderColor: tk.border, background: tk.surface }}>
                                                <div className="text-[15px] font-bold mb-3" style={{ color: tk.ink }}>Response Breakdown ({reportTimeframe})</div>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                                                    <Chip icon={CheckCircle2} label="Complete" value={completeCount} bg={tk.mintBg} color={tk.mintText} />
                                                    <Chip icon={CircleDashed} label="Partial" value={partialCount} bg={tk.amberBg} color={tk.amberText} />
                                                    <Chip icon={UserX} label="Anonymous" value={anonCount} bg={tk.blueBg} color={tk.blueText} />
                                                    <Chip icon={Users} label="Logged-in" value={loggedInCount} bg={tk.purpleBg} color={tk.purpleText} />
                                                    {selectedId
                                                        ? <Chip icon={FileText} label="Questions" value={questions.length} bg={tk.greyBg} color={tk.muted} />
                                                        : <Chip icon={FileText} label="Surveys" value={new Set(records.map(r => r.SurveyID)).size} bg={tk.greyBg} color={tk.muted} />}
                                                    <Chip icon={Database} label="Total" value={records.length} bg={tk.redBg} color={tk.redText} />
                                                </div>
                                            </div>

                                            {/* activity chart */}
                                            <div className="rounded-xl border p-5" style={{ borderColor: tk.border, background: tk.surface }}>
                                                <div className="text-[15px] font-bold mb-2" style={{ color: tk.ink }}>Response Activity ({reportTimeframe})</div>
                                                {activityData.length === 0 ? (
                                                    <div className="py-12 text-center text-sm" style={{ color: tk.muted }}>
                                                        {selectedId ? `No responses yet for "${surveyMeta?.Title || 'this survey'}".` : 'No responses in this timeframe yet.'}
                                                    </div>
                                                ) : (
                                                    <ResponsiveContainer width="100%" height={220}>
                                                        <LineChart data={activityData}>
                                                            <CartesianGrid stroke={tk.border} strokeDasharray="4 4" />
                                                            <XAxis dataKey="day" tick={{ fontSize: 12, fill: tk.muted }} />
                                                            <YAxis tick={{ fontSize: 12, fill: tk.muted }} allowDecimals={false} />
                                                            <ChartTooltip contentStyle={{ background: tk.surface, borderColor: tk.border, color: tk.ink }} />
                                                            <Legend />
                                                            <Line type="monotone" dataKey="Complete" stroke="#2F7D52" strokeWidth={2} dot={{ r: 4 }} />
                                                            <Line type="monotone" dataKey="Partial" stroke="#D97706" strokeWidth={2} dot={{ r: 4 }} />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                )}
                                            </div>

                                            {/* per-question breakdown table (specific survey only) */}
                                            {selectedId && (
                                            <div className="rounded-xl border p-5" style={{ borderColor: tk.border, background: tk.surface }}>
                                                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                                    <div className="flex items-center gap-2 text-[15px] font-bold" style={{ color: tk.ink }}>
                                                        <ListChecks size={16} /> Question Breakdown ({surveyMeta?.Title || ''})
                                                    </div>
                                                    <input
                                                        className={`${inputCls} max-w-[220px]`}
                                                        placeholder="Search question…"
                                                        value={reportTableQuery}
                                                        onChange={e => setReportTableQuery(e.target.value)}
                                                    />
                                                </div>
                                                <div className="overflow-x-auto rounded-lg border" style={{ borderColor: tk.border }}>
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr style={{ background: tk.bg, color: tk.muted }}>
                                                                {[
                                                                    { key: 'question', label: 'QUESTION' },
                                                                    { key: 'type', label: 'TYPE' },
                                                                    { key: 'responses', label: 'RESPONSES' },
                                                                    { key: 'topAnswer', label: 'TOP ANSWER' },
                                                                    { key: 'responseRate', label: 'RESPONSE RATE' },
                                                                ].map(col => (
                                                                    <th key={col.key}
                                                                        className="text-left font-semibold text-xs tracking-wide px-4 py-2.5 cursor-pointer select-none"
                                                                        onClick={() => toggleSort(col.key)}>
                                                                        <span className="inline-flex items-center gap-1">
                                                                            {col.label} {sortIcon(reportSort.key === col.key ? reportSort.dir : null)}
                                                                        </span>
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {sortedRows.map((r, i) => (
                                                                <tr key={r.id} className="border-t" style={{ borderColor: tk.border, background: i % 2 ? tk.bg : tk.surface }}>
                                                                    <td className="px-4 py-2.5 font-medium" style={{ color: tk.ink }}>{r.question}</td>
                                                                    <td className="px-4 py-2.5" style={{ color: tk.muted }}>{r.type}</td>
                                                                    <td className="px-4 py-2.5" style={{ color: tk.ink }}>{r.responses}</td>
                                                                    <td className="px-4 py-2.5" style={{ color: tk.ink }}>{r.topAnswer}</td>
                                                                    <td className="px-4 py-2.5">
                                                                        <Pill bg={tk.mintBg} color={tk.mintText}>{r.responseRate}%</Pill>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            {sortedRows.length === 0 && (
                                                                <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: tk.muted }}>
                                                                    {reportTableQuery ? `No questions match "${reportTableQuery}".` : 'No questions found.'}
                                                                </td></tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                            )}

                                            {/* cross-survey ranked list */}
                                            <div className="rounded-xl border p-5" style={{ borderColor: tk.border, background: tk.surface }}>
                                                <div className="text-[15px] font-bold mb-3" style={{ color: tk.ink }}>Top Surveys by Response Count ({reportTimeframe})</div>
                                                <div className="space-y-1.5">
                                                    {topSurveys.map((s, i) => (
                                                        <div key={s.id} className="flex items-center justify-between rounded-lg px-3 py-2.5" style={{ background: tk.bg }}>
                                                            <div className="flex items-center gap-3">
                                                                <span className="flex items-center justify-center h-7 w-7 rounded-full text-white text-sm font-bold" style={{ background: tk.primary }}>{i + 1}</span>
                                                                <span className="text-sm font-medium" style={{ color: tk.ink }}>{s.title}</span>
                                                            </div>
                                                            <Pill bg={tk.mintBg} color={tk.mintText}>{s.responses} response{s.responses === 1 ? '' : 's'}</Pill>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                    </>
                                </div>
                            );
                        })()}
                    </div>
                )}


                {/* ── TAB: Builder ── */}
                {activeTab === 'builder' && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                        {/* Left — form */}
                        <div className="space-y-5">

                            {/* Survey meta */}
                            <div className={`rounded-2xl border p-5 ${cardBg}`}>
                                <h2 className="font-bold mb-4">Survey Details</h2>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">Title *</label>
                                        <input
                                            className={inputCls}
                                            placeholder="e.g. Onboarding Survey"
                                            value={formData.title}
                                            onChange={e => setFormData({ ...formData, title: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">Description</label>
                                        <textarea
                                            className={`${inputCls} h-20 resize-none`}
                                            placeholder="Optional short description shown to users"
                                            value={formData.description}
                                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">Survey Type</label>
                                            <select className={inputCls} value={formData.surveyType} onChange={e => setFormData({ ...formData, surveyType: e.target.value })}>
                                                {SURVEY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">Trigger</label>
                                            <select className={inputCls} value={formData.triggerType} onChange={e => setFormData({ ...formData, triggerType: e.target.value, triggerValue: '' })}>
                                                {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    {triggerMeta?.hasValue && (
                                        <div>
                                            <label className="block text-xs font-medium mb-1 text-slate-500 uppercase tracking-wide">
                                                {formData.triggerType === 'login_count' ? 'Login count threshold' : 'Days threshold'}
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                className={inputCls}
                                                placeholder={triggerMeta.placeholder}
                                                value={formData.triggerValue}
                                                onChange={e => setFormData({ ...formData, triggerValue: e.target.value })}
                                            />
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="survey-active"
                                            checked={formData.isActive}
                                            onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                            className="rounded"
                                        />
                                        <label htmlFor="survey-active" className="text-sm">Active (visible to users)</label>
                                    </div>
                                </div>
                            </div>

                            {/* Questions */}
                            <div className={`rounded-2xl border p-5 ${cardBg}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="font-bold">Questions ({questions.length})</h2>
                                    {!showQuestionForm && (
                                        <button
                                            onClick={() => { setEditingQuestionIdx(null); setShowQuestionForm(true); }}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                        >
                                            <Plus size={14} /> Add Question
                                        </button>
                                    )}
                                </div>

                                {/* Add question form */}
                                {showQuestionForm && (
                                    <div className="mb-4">
                                        <QuestionForm
                                            isDarkMode={isDarkMode}
                                            onSave={saveQuestion}
                                            onCancel={() => setShowQuestionForm(false)}
                                            existingKeys={questions.map(q => q.name).filter(Boolean)}
                                        />
                                    </div>
                                )}

                                {/* Question list */}
                                {questions.length === 0 && !showQuestionForm ? (
                                    <p className="text-sm text-slate-400 text-center py-6">No questions yet. Click "Add Question" to start.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {questions.map((q, idx) => (
                                            <div key={idx}>
                                                {editingQuestionIdx === idx ? (
                                                    <QuestionForm
                                                        key={q.name + idx}
                                                        isDarkMode={isDarkMode}
                                                        initial={q}
                                                        onSave={saveQuestion}
                                                        onCancel={() => setEditingQuestionIdx(null)}
                                                        existingKeys={questions.filter((_, i) => i !== idx).map(q => q.name).filter(Boolean)}
                                                    />
                                                ) : (
                                                    <div className={`flex items-start gap-3 p-3 rounded-xl border ${isDarkMode ? 'border-slate-600 bg-slate-700/40' : 'border-slate-200 bg-slate-50'}`}>
                                                        <div className="flex flex-col gap-0.5">
                                                            <button onClick={() => moveQuestion(idx, 'up')} disabled={idx === 0} className="text-slate-400 disabled:opacity-20 hover:text-primary-500">
                                                                <ChevronUp size={14} />
                                                            </button>
                                                            <button onClick={() => moveQuestion(idx, 'down')} disabled={idx === questions.length - 1} className="text-slate-400 disabled:opacity-20 hover:text-primary-500">
                                                                <ChevronDown size={14} />
                                                            </button>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium truncate">{q.title || '(no title)'}</p>
                                                            <p className="text-xs text-slate-400 mt-0.5">
                                                                {QUESTION_TYPES.find(t => t.value === q.type)?.label || q.type}
                                                                {q.isRequired && <span className="ml-2 text-red-400">Required</span>}
                                                            </p>
                                                        </div>
                                                        <div className="flex gap-1">
                                                            <button onClick={() => { setEditingQuestionIdx(idx); setShowQuestionForm(false); }} className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-primary-600 transition-colors">
                                                                <Pencil size={13} />
                                                            </button>
                                                            <button onClick={() => deleteQuestion(idx)} className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors">
                                                                <X size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Save / feedback */}
                            {saveError && (
                                <div className="flex items-center gap-2 text-red-500 text-sm px-1">
                                    <AlertCircle size={15} /> {saveError}
                                </div>
                            )}
                            {saveSuccess && (
                                <div className="text-primary-600 text-sm font-semibold px-1">
                                    ✓ Survey saved successfully!
                                </div>
                            )}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setActiveTab('surveys')}
                                    className={`px-4 py-2 text-sm rounded-xl border font-medium transition-colors ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex items-center gap-2 px-5 py-2 text-sm bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-60 transition-colors"
                                >
                                    <Save size={15} />
                                    {saving ? 'Saving…' : (editingId ? 'Update Survey' : 'Create Survey')}
                                </button>
                            </div>
                        </div>

                        {/* Right — live preview */}
                        <div className={`rounded-2xl border p-5 h-fit sticky top-6 ${cardBg}`}>
                            <div className="flex items-center gap-2 mb-4">
                                <Eye size={16} className="text-primary-500" />
                                <h2 className="font-bold">Live Preview</h2>
                            </div>
                            {previewModel ? (
                                <div className="overflow-y-auto max-h-[70vh]">
                                    <Survey model={previewModel} />
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 text-center py-12">
                                    Add a title and questions to see a live preview here.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* ── TAB: Career Success Index ── */}
                {activeTab === 'csi' && <CSIManagement />}
            </div>
        </div>
    );
}
